import { useState, useEffect } from 'react';
import { Filter, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { asArray } from '../lib/safeData';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';

const SEVERITY_CONFIG = {
  success: { color: '#10B981', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.2)', label: 'SUCCESS' },
  info: { color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.2)', label: 'INFO' },
  warning: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', label: 'WARNING' },
  error: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.2)', label: 'ERROR' },
};

const EVENT_LABELS = {
  health_check: 'Health Check',
  automation: 'Automation',
  post_generated: 'Post Generated',
  post_approved: 'Post Approved',
  deployment: 'Deployment',
};

export default function Activity() {
  const [events, setEvents] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ app_id: '', severity: '', event_type: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.app_id) params.append('app_id', filters.app_id);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.event_type) params.append('event_type', filters.event_type);
      params.append('limit', '100');

      const [eventsRes, appsRes] = await Promise.all([
        api.get(`/activity/?${params}`),
        api.get('/apps/'),
      ]);
      setEvents(asArray(eventsRes.data));
      setApps(asArray(appsRes.data));
    } catch { toast.error('Failed to load activity'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filters]);

  const clearFilters = () => setFilters({ app_id: '', severity: '', event_type: '' });

  return (
    <div className="space-y-6 fade-in" data-testid="activity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Activity Feed</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{events.length} events</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-primary/30 text-muted-foreground hover:text-foreground text-sm transition-colors" data-testid="refresh-activity-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl border border-white/5 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <select
            value={filters.app_id}
            onChange={e => setFilters({ ...filters, app_id: e.target.value })}
            className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-foreground outline-none"
            data-testid="filter-app-select"
          >
            <option value="">All Apps</option>
            {apps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select
            value={filters.severity}
            onChange={e => setFilters({ ...filters, severity: e.target.value })}
            className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-foreground outline-none"
            data-testid="filter-severity-select"
          >
            <option value="">All Severities</option>
            {Object.entries(SEVERITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select
            value={filters.event_type}
            onChange={e => setFilters({ ...filters, event_type: e.target.value })}
            className="bg-secondary/30 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-foreground outline-none"
            data-testid="filter-event-type-select"
          >
            <option value="">All Types</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {(filters.app_id || filters.severity || filters.event_type) && (
            <button onClick={clearFilters} className="text-xs text-primary hover:text-primary/80 transition-colors" data-testid="clear-filters-btn">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : events.length === 0 ? (
        <div className="glass rounded-xl border border-white/5 p-16 text-center">
          <p className="text-muted-foreground">No activity events found.</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-5 top-0 bottom-0 w-px bg-border/40" />
          <div className="space-y-4">
            {events.map(event => {
              const sev = SEVERITY_CONFIG[event.severity] || SEVERITY_CONFIG.info;
              return (
                <div key={event.id} className="flex gap-4 pl-2 fade-in" data-testid={`activity-event-${event.id}`}>
                  {/* Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center border text-xs font-bold"
                      style={{ background: sev.bg, borderColor: sev.border, color: sev.color }}
                    >
                      {event.severity === 'success' ? '✓' : event.severity === 'error' ? '✗' : event.severity === 'warning' ? '!' : 'i'}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 glass rounded-xl border border-white/5 p-4 hover:border-primary/10 transition-colors">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1">
                        <p className="text-sm text-foreground">{event.message}</p>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <span
                            className="text-xs px-2 py-0.5 rounded-full border font-mono uppercase tracking-wide"
                            style={{ background: sev.bg, borderColor: sev.border, color: sev.color }}
                          >
                            {sev.label}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary/50 text-muted-foreground font-mono">
                            {EVENT_LABELS[event.event_type] || event.event_type}
                          </span>
                          {event.app_name && (
                            <span className="text-xs text-muted-foreground font-mono">{event.app_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs text-muted-foreground font-mono">
                          {formatDistanceToNow(new Date(event.created_at), { addSuffix: true })}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">
                          {format(new Date(event.created_at), 'HH:mm:ss')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
